import React, { useState } from "react";
import axios from "axios";
import { Star, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewForm = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    location: "",
    rating: 0,
    text: "",
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const reset = () =>
    setForm({ name: "", location: "", rating: 0, text: "" });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.location.trim() || !form.text.trim()) {
      toast({
        title: "Missing info",
        description: "Please fill in your name, location, and review.",
      });
      return;
    }
    if (form.rating < 1) {
      toast({
        title: "Pick a star rating",
        description: "Tap 1 to 5 stars to rate your experience.",
      });
      return;
    }
    if (form.text.trim().length < 10) {
      toast({
        title: "Review too short",
        description: "Please share at least a sentence about your experience.",
      });
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/reviews`, {
        name: form.name.trim(),
        location: form.location.trim(),
        rating: form.rating,
        text: form.text.trim(),
      });
      toast({
        title: "Thanks for the review!",
        description:
          "Shane will review it and it'll show up on the site once approved.",
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Couldn't submit",
        description: "Something went wrong. Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const activeRating = hoverRating || form.rating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-red-900/50 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-2xl font-extrabold">
            Leave a Review
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Share your experience with 1/2 Fast Mobile Keys. Shane will review
            it before it goes live.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <label className="text-sm font-semibold text-zinc-200">
              Your Name
            </label>
            <Input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Jane D."
              className="mt-1.5 h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600"
              maxLength={80}
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-zinc-200">
              Location
            </label>
            <Input
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="Louisa, KY"
              className="mt-1.5 h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600"
              maxLength={80}
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-zinc-200">
              Rating
            </label>
            <div className="mt-2 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  <Star
                    className={`w-8 h-8 transition-colors ${
                      n <= activeRating
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-zinc-700"
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-zinc-400 text-sm">
                {form.rating > 0 ? `${form.rating}/5` : "Tap to rate"}
              </span>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-zinc-200">
              Your Review
            </label>
            <Textarea
              name="text"
              value={form.text}
              onChange={handleChange}
              placeholder="What was your experience like with Shane?"
              className="mt-1.5 min-h-[110px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600"
              maxLength={2000}
            />
            <div className="text-xs text-zinc-500 mt-1 text-right">
              {form.text.length}/2000
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-brand-red text-white font-semibold disabled:opacity-70"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? "Sending..." : "Submit Review"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewForm;
